import { useState, useEffect } from 'react';
import { usePrevious } from '@uidotdev/usehooks';
import { useMutation, useQuery } from '@tanstack/react-query';
import { useAccount, useWaitForTransactionReceipt } from 'wagmi';
import { toast } from 'react-toastify';
import { Button } from '@/ui/PrimaryButton';
import { useBlockExplorerURL } from '@/utils/url/blockExplorer';
import { garnetHolesky } from '@/chains/garnetHolesky';
import { useConnectModal } from '@rainbow-me/rainbowkit';
import { Disclaimer } from '../Disclaimer';
import { activeChainId } from '@/common';
import { pyrope } from '@/chains/pyrope';

export function FaucetClaim() {
  const { address } = useAccount();
  const { openConnectModal } = useConnectModal();
  const [isWaitingTx, setIsWaitingTx] = useState(false);

  const {
    data: faucetData,
    isLoading: isLoadingFaucet,
    refetch,
  } = useQuery({
    queryKey: ['faucet', address],
    queryFn: async () => {
      const res = await fetch(`/api/faucet?to=${address}`);
      return res.json();
    },
    enabled: Boolean(address),
  });

  const {
    data,
    mutate: claim,
    isPending: isPendingClaim,
  } = useMutation({
    mutationFn: async () => {
      setIsWaitingTx(true);
      const res = await fetch('/api/faucet', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ to: address }),
      });

      const data = await res.json();
      if (!res.ok) {
        toast.error(data.error);
        setIsWaitingTx(false);
      } else {
        toast.info('Transaction submitted. Waiting for confirmations...');
      }
      return data;
    },
  });

  const isPending = isPendingClaim || isWaitingTx || isLoadingFaucet;
  const isDisabled =
    Boolean(address) && (isPending || Boolean(faucetData?.error) || faucetData?.claimed);

  const txUrl = useBlockExplorerURL(garnetHolesky.id, data?.hash);
  const waitForReceipt = useWaitForTransactionReceipt({
    hash: data?.hash,
    chainId: activeChainId === garnetHolesky.id ? garnetHolesky.id : pyrope.id,
  });
  const prevTxStatus = usePrevious(waitForReceipt.data?.status);

  useEffect(() => {
    if (!prevTxStatus && waitForReceipt.data?.status === 'success') {
      toast.success('Transaction confirmed');
    } else if (!prevTxStatus && waitForReceipt.data?.status === 'reverted') {
      toast.error('Transaction failed');
    }

    refetch();
    setIsWaitingTx(false);
  }, [prevTxStatus, waitForReceipt.data?.status, refetch]);

  const onSubmit = async (evt: React.FormEvent<HTMLFormElement>) => {
    evt.preventDefault();

    if (isDisabled) {
      return;
    }

    if (!address) {
      if (!openConnectModal) {
        throw new Error('Missing openConnectModal');
      }
      return openConnectModal();
    }
    await claim();
  };

  return (
    <>
      <Disclaimer>
        <p>
          Faucet has a cooldown period of 24 hours per wallet address, and a total of{' '}
          {process.env.NEXT_PUBLIC_DAILY_LIMIT as string} ETH daily limit.
        </p>
      </Disclaimer>

      <form onSubmit={onSubmit} className="mt-8">
        <div className="inline-block text-center">
          <Button
            type="submit"
            className="px-6"
            label={
              !address
                ? 'Connect wallet'
                : faucetData?.error
                  ? faucetData.error
                  : `Receive ${process.env.NEXT_PUBLIC_DRIP_VALUE as string} ${
                      activeChainId === garnetHolesky.id ? 'Garnet' : 'Pyrope'
                    } ETH`
            }
            pending={isPending}
            disabled={isDisabled}
          />

          {address && data?.hash && activeChainId === garnetHolesky.id && (
            <a href={txUrl} target="_blank" rel="noreferrer">
              <p className="mt-3 text-white/80">View on block explorer</p>
            </a>
          )}
        </div>
      </form>
    </>
  );
}
